import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchJobcostings,
    createJobCosting,
    updateJobCosting,
    deleteJobCosting,
    setPagination,
    setSort,
    searchJobcostings,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/jobcosting/jobcostingActions';
import JobCostingForm from '../../components/pageModule/Jobcostings/JobCostingForm';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import jobcostingService from '../../services/jobcostingService';
import { useHasPermission } from '../../utils/permissionUtils';
const MySwal = withReactContent(Swal);

const Jobcostings = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [] }) => {

    const hasCreatePermission = useHasPermission(['create job costings']);
    const hasUpdatePermission = useHasPermission(['update job costings']);
    const hasViewPermission = useHasPermission(['view job costings']);

    const jobcostingState = useSelector(state => state.jobcosting);
    const dispatch = useDispatch();
    const [cloudLoadingByItem, setCloudLoadingByItem] = useState({});

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createJobCosting(formData));
            toast.success('JobCosting created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating jobcosting', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateJobCosting(id, formData));
            toast.success('JobCosting updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating jobcosting', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteJobCosting(id));
                toast.success('JobCosting deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting jobcosting', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Job costing cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingByItem(prev => ({ ...prev, [row.id]: true }));
        try {
            const response = await jobcostingService.pushCloud(row.id, {});
            toast.success(response.message || 'Job costing queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchJobcostings({}, {}, true, externalCondition));
        } catch (error) {
            toast.error(error.message || 'Error pushing job costing to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingByItem(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch, externalCondition]);

    const columns = useMemo(() => [
        {
            id: 'costing_number',
            name: 'Costing Number',
            selector: row => row.costing_number,
            minWidth: '170px'
        },
        {
            id: 'job_order_number',
            name: 'Job Order',
            selector: row => row.job_order_number,
            minWidth: '170px'
        },
        {
            id: 'costing_date',
            name: 'Costing Date',
            selector: row => row.costing_date,
            sortable: true,
            sortField: 'job_costings.costing_date',
        },
        {
            id: 'customer_name',
            name: 'Customer Name',
            selector: row => row.customer_name,
        },
        {
            id: 'lpo_number',
            name: 'LPO Number',
            selector: row => (row?.is_lpo ? row.lpo_number : '-'),
        },
        {
            id: 'total_production',
            name: 'Total Production',
            selector: row => row.total_production,
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '140px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingByItem[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingByItem]);


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchJobcostings(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchJobcostings(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Job Costing"
                moduleName="JobCosting"
                moduleKey="jobcostings"
                state={jobcostingState}
                actions={actions}
                FormComponent={JobCostingForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="180px"
                urlKey="job_costings"
                actionType="url"
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}

                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Jobcostings; 